import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Badge } from './ui/badge';
import { Camera, Images, Upload, X, ZoomIn, Trash2, Plus } from 'lucide-react';
import { toast } from 'sonner';

const CarPhotoGallery = ({ car, onPhotoAdd, onPhotoDelete, trigger, userPlan }) => {
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [uploadingPhotos, setUploadingPhotos] = useState([]);
  
  // Check if user has photo gallery access (Premium or Professional)
  const hasPhotoGalleryAccess = userPlan === 'Premium' || userPlan === 'Professional';

  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    // Check if user has access before proceeding
    if (!hasPhotoGalleryAccess) {
      toast.error('Photo Gallery is a Premium feature. Upgrade to Premium to upload photos.', {
        duration: 5000,
        icon: '🔒',
      });
      event.target.value = ''; // Clear the file input
      return;
    }

    setUploadingPhotos(files.map(file => ({ file, uploading: true })));

    for (const file of files) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        console.error('Invalid file type');
        continue;
      }

      // Validate file size (max 10MB per photo)
      if (file.size > 10 * 1024 * 1024) {
        console.error('File too large');
        continue;
      }

      try {
        const reader = new FileReader();
        reader.onload = async (e) => {
          const base64Data = e.target.result.split(',')[1];
          const photoData = {
            data: base64Data,
            content_type: file.type,
            filename: file.name
          };

          if (onPhotoAdd) {
            await onPhotoAdd(photoData);
          }
        };
        reader.readAsDataURL(file);
      } catch (error) {
        console.error('Error uploading photo:', error);
      }
    }

    setUploadingPhotos([]);
    // Clear the file input
    event.target.value = '';
  };

  const handleDeletePhoto = (photoId) => {
    // Check if user has access before proceeding
    if (!hasPhotoGalleryAccess) {
      toast.error('Photo Gallery is a Premium feature. Upgrade to Premium to manage photos.', {
        duration: 5000,
        icon: '🔒',
      });
      return;
    }
    
    if (onPhotoDelete) {
      onPhotoDelete(photoId);
    }
  };

  const PhotoGrid = ({ photos }) => (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
      {photos.map((photo, index) => (
        <div key={photo.id || index} className="relative group cursor-pointer">
          <div 
            className="aspect-square bg-gray-100 rounded-lg overflow-hidden border hover:border-blue-300 transition-all"
            onClick={() => setSelectedPhoto(photo)}
          >
            <img
              src={`data:${photo.content_type};base64,${photo.data}`}
              alt={photo.filename || `Photo ${index + 1}`}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
            />
          </div>
          
          {/* Photo overlay with actions */}
          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-200 rounded-lg flex items-center justify-center">
            <div className="opacity-0 group-hover:opacity-100 transition-opacity flex space-x-2">
              <Button
                size="sm"
                variant="secondary"
                className="bg-white bg-opacity-90 hover:bg-opacity-100"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedPhoto(photo);
                }}
              >
                <ZoomIn className="w-4 h-4" />
              </Button>
              <Button
                size="sm"
                variant="destructive"
                className="bg-red-600 bg-opacity-90 hover:bg-opacity-100"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeletePhoto(photo.id);
                }}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
          
          {/* Photo filename */}
          <div className="mt-1 text-xs text-gray-500 truncate">
            {photo.filename || `Photo ${index + 1}`}
          </div>
        </div>
      ))}
      
      {/* Upload new photos button - visible to all users, backend will enforce restrictions */}
      <div className="aspect-square border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center hover:border-blue-400 hover:bg-blue-50 transition-all cursor-pointer group">
        <label htmlFor="gallery-upload" className="cursor-pointer w-full h-full flex items-center justify-center">
          <div className="text-center">
            <Plus className="w-8 h-8 text-gray-400 group-hover:text-blue-500 mx-auto mb-2" />
            <span className="text-sm text-gray-500 group-hover:text-blue-600">Add Photos</span>
          </div>
        </label>
        <input
          id="gallery-upload"
          type="file"
          accept="image/*"
          multiple
          onChange={handleFileSelect}
          className="hidden"
        />
      </div>
    </div>
  );

  const PhotoViewer = ({ photo, onClose }) => (
    <Dialog open={!!photo} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>{photo?.filename || 'Car Photo'}</span>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </DialogTitle>
        </DialogHeader>
        
        {photo && (
          <div className="mt-4">
            <img
              src={`data:${photo.content_type};base64,${photo.data}`}
              alt={photo.filename || 'Car Photo'}
              className="w-full max-h-[70vh] object-contain rounded-lg"
            />
            
            <div className="mt-4 flex justify-between items-center">
              <div className="text-sm text-gray-600">
                <span>File: {photo.filename}</span>
                {photo.created_at && (
                  <span className="ml-4">
                    Added: {new Date(photo.created_at).toLocaleDateString()}
                  </span>
                )}
              </div>
              
              <Button
                variant="destructive"
                size="sm"
                onClick={() => {
                  handleDeletePhoto(photo.id);
                  onClose();
                }}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Photo
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );

  const defaultTrigger = (
    <Button variant="outline" className="border-purple-200 text-purple-700 hover:bg-purple-50">
      <Images className="w-4 h-4 mr-2" />
      Photo Gallery
      {car?.gallery && car.gallery.length > 0 && (
        <Badge className="ml-2 bg-purple-100 text-purple-800">
          {car.gallery.length}
        </Badge>
      )}
    </Button>
  );

  return (
    <>
      <Dialog>
        <DialogTrigger asChild>
          {trigger || defaultTrigger}
        </DialogTrigger>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <Camera className="w-5 h-5 mr-2 text-purple-600" />
              Photo Gallery
              {car?.name && (
                <span className="ml-2 text-blue-600">- {car.name}</span>
              )}
            </DialogTitle>
            <DialogDescription>
              {car?.year} {car?.make} {car?.model} photo collection
            </DialogDescription>
          </DialogHeader>
          
          <div className="mt-4">
            {/* Upgrade Banner for Free/Basic Users */}
            {!hasPhotoGalleryAccess && (
              <div className="mb-6 p-4 bg-orange-50 border-2 border-orange-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                      <span className="text-xl">🔒</span>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-orange-900 mb-1">Upgrade to Premium Required</h4>
                    <p className="text-sm text-orange-700">
                      Photo Gallery uploads are a Premium feature. You can view photos, but uploading and managing photos requires a Premium subscription.
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Main car photo section */}
            {car?.photo && (
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-3 flex items-center">
                  <Camera className="w-5 h-5 mr-2 text-blue-600" />
                  Main Car Photo
                </h3>
                <div className="relative inline-block">
                  <img
                    src={`data:${car.photo.content_type};base64,${car.photo.data}`}
                    alt="Main car photo"
                    className="w-48 h-32 object-cover rounded-lg border-2 border-blue-200 cursor-pointer hover:border-blue-400 transition-all"
                    onClick={() => setSelectedPhoto(car.photo)}
                  />
                  <Badge className="absolute top-2 right-2 bg-blue-600 text-white">
                    Main
                  </Badge>
                </div>
              </div>
            )}
            
            {/* Photo gallery section */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold flex items-center">
                  <Images className="w-5 h-5 mr-2 text-purple-600" />
                  Photo Gallery
                  {car?.gallery && (
                    <Badge className="ml-2 bg-purple-100 text-purple-800">
                      {car.gallery.length} photos
                    </Badge>
                  )}
                </h3>
              </div>
              
              {car?.gallery && car.gallery.length > 0 ? (
                <PhotoGrid photos={car.gallery} />
              ) : (
                <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
                  <Images className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500 mb-4">No photos in gallery yet</p>
                  <label htmlFor="gallery-upload-empty" className="cursor-pointer">
                    <Button asChild variant="outline" className="border-purple-200 text-purple-700 hover:bg-purple-50">
                      <span>
                        <Upload className="w-4 h-4 mr-2" />
                        Upload First Photos
                      </span>
                    </Button>
                  </label>
                  <input
                    id="gallery-upload-empty"
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </div>
              )}
            </div>
            
            {/* Upload progress */}
            {uploadingPhotos.length > 0 && (
              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center text-blue-800">
                  <Upload className="w-4 h-4 mr-2 animate-pulse" />
                  Uploading {uploadingPhotos.length} photo(s)...
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Photo viewer */}
      <PhotoViewer 
        photo={selectedPhoto} 
        onClose={() => setSelectedPhoto(null)} 
      />
    </>
  );
};

export default CarPhotoGallery;